import React from 'react'
import { useI18n } from '../../i18n'
import LifecycleBadge from './LifecycleBadge'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'
import type { GrcSummaryRow } from '../../services/informationSystemComplianceApi'
import { readinessBadgeClass } from './riskComplianceRowParts'

interface Props {
  row: GrcSummaryRow
  onClose: () => void
  onOpenPassport?: () => void
  onOpenGraph?: () => void
}

export default function RiskComplianceDetailPanel({ row, onClose, onOpenPassport, onOpenGraph }: Props) {
  const { t } = useI18n()
  const unknown = t('common.unknown', '—')
  const notAvailable = t('assets.riskCompliance.notAvailable', 'N/A')
  const status = row.readinessStatus || 'UNKNOWN'

  const kpis = [
    { label: t('assets.grc.complianceScope', 'Standards in scope'), value: row.applicableStandardsCount },
    {
      label: t('assets.grc.controlCoverage', 'Control coverage'),
      value: row.controlCoveragePercent != null ? `${row.controlCoveragePercent}%` : notAvailable,
    },
    { label: t('assets.grc.controlsMapped', 'Controls mapped'), value: row.controlsTotal },
    { label: t('assets.grc.evidenceCount', 'Evidence linked'), value: row.evidenceLinkedCount },
    { label: t('assets.grc.remediationOpen', 'Open remediation'), value: row.remediationOpenCount },
    { label: t('assets.riskCompliance.riskScore', 'Risk score'), value: notAvailable },
  ]

  return (
    <div className={styles.detailsPanel}>
      <header className={styles.detailsPanelHeader}>
        <div>
          <p className={styles.detailsPanelLabel}>{t('assets.riskCompliance.selectedSystem', 'Selected system')}</p>
          <div className={styles.identityHeader}>
            <h3 className={styles.detailsPanelTitle}>{row.name}</h3>
            <LifecycleBadge status={row.lifecycleStatus} />
          </div>
          <span className={`${styles.readinessBadge} ${readinessBadgeClass(status)}`}>
            {t(`assets.grc.readiness.${status}`, status)}
          </span>
        </div>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('assets.postureRow.closeDetails', 'Close')}>
          {t('assets.postureRow.closeDetails', 'Close')}
        </button>
      </header>
      <div className={styles.detailsPanelBody}>
        <div className={styles.detailsKpiGrid}>
          {kpis.map((k) => (
            <div key={k.label} className={styles.detailsKpi}>
              <p className={styles.detailsKpiLabel}>{k.label}</p>
              <p className={styles.detailsKpiValue}>{k.value ?? unknown}</p>
            </div>
          ))}
        </div>
        <p className={styles.sectionHeader}>{t('assets.grc.controls', 'Control breakdown')}</p>
        <ul className={styles.explanationList}>
          <li>
            {t('assets.grc.implementation.IMPLEMENTED', 'Implemented')}: {row.controlsImplemented}
          </li>
          <li>
            {t('assets.grc.implementation.PARTIAL', 'Partial')}: {row.controlsPartial}
          </li>
          <li>
            {t('assets.grc.implementation.NOT_STARTED', 'Not started')}: {row.controlsNotStarted}
          </li>
          {row.evidenceVerifiedCount != null ? (
            <li>
              {t('assets.grc.verified', 'Verified evidence')}: {row.evidenceVerifiedCount}
            </li>
          ) : null}
        </ul>
        {(row.explanation || []).length > 0 ? (
          <>
            <p className={styles.sectionHeader}>{t('assets.riskCompliance.readinessNotes', 'Readiness notes')}</p>
            <ul className={styles.explanationList}>
              {row.explanation.map((ex) => (
                <li key={ex.code}>{t(`assets.grc.reason.${ex.code}`, ex.message)}</li>
              ))}
            </ul>
          </>
        ) : null}
        <div className={styles.panelActions}>
          {onOpenPassport ? (
            <button type="button" className={styles.rowActionBtn} onClick={onOpenPassport}>
              {t('assets.grc.openPassport', 'Open passport')}
            </button>
          ) : null}
          {onOpenGraph ? (
            <button type="button" className={styles.rowActionBtn} onClick={onOpenGraph}>
              {t('assets.graph.openGraph', 'Open graph')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function RiskComplianceDetailEmpty() {
  const { t } = useI18n()
  return (
    <div className={styles.detailsPanelEmpty}>
      {t('assets.riskCompliance.selectSystemHint', 'Select a system to view GRC readiness details.')}
    </div>
  )
}
